import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

const ROLE_LABELS = {
  owner_admin: "Owner/Admin",
  office_admin: "Office/Admin",
  project_manager: "Project Manager",
  supervisor: "Supervisor",
};

const OWNER_ADMIN_FLOOR = 1;

const apiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

export default function UserLimitsPanel() {
  const [matrix, setMatrix] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [companyId]: { [role]: "" | "n" } }
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [rowError, setRowError] = useState({}); // { [companyId]: msg }
  const [rowFlash, setRowFlash] = useState({});  // { [companyId]: "saved" }

  const load = useCallback(async () => {
    setError("");
    try {
      const response = await axios.get(`${API}/companies/role-limits-matrix`);
      setMatrix(response.data);
      const nextDrafts = {};
      for (const row of response.data.rows || []) {
        nextDrafts[row.company_id] = {};
        for (const role of response.data.role_keys || []) {
          const v = row.limits?.[role];
          nextDrafts[row.company_id][role] = v === null || v === undefined ? "" : String(v);
        }
      }
      setDrafts(nextDrafts);
    } catch (err) {
      setError(apiErrorMessage(err, "Role limits could not be loaded."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(); };

  const changeDraft = (companyId, role, value) => {
    setDrafts((prev) => ({ ...prev, [companyId]: { ...prev[companyId], [role]: value } }));
    setRowError((prev) => ({ ...prev, [companyId]: "" }));
    setRowFlash((prev) => ({ ...prev, [companyId]: "" }));
  };

  const rowIsDirty = (row) => {
    const draft = drafts[row.company_id] || {};
    return (matrix?.role_keys || []).some((role) => {
      const stored = row.limits?.[role];
      const storedStr = stored === null || stored === undefined ? "" : String(stored);
      return (draft[role] ?? "") !== storedStr;
    });
  };

  const buildPayload = (row) => {
    const draft = drafts[row.company_id] || {};
    const changes = {};
    for (const role of matrix?.role_keys || []) {
      const stored = row.limits?.[role];
      const storedStr = stored === null || stored === undefined ? "" : String(stored);
      const draftStr = (draft[role] ?? "").trim();
      if (draftStr !== storedStr) {
        changes[role] = draftStr === "" ? null : Number(draftStr);
      }
    }
    return changes;
  };

  const saveRow = async (row) => {
    const payload = buildPayload(row);
    if (Object.keys(payload).length === 0) return;
    setSavingId(row.company_id);
    setRowError((prev) => ({ ...prev, [row.company_id]: "" }));
    try {
      const response = await axios.patch(
        `${API}/companies/${row.company_id}/role-limits`,
        payload,
      );
      setMatrix((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) =>
            r.company_id === row.company_id
              ? { ...r, limits: response.data.limits, usage: response.data.usage }
              : r,
          ),
        };
      });
      setRowFlash((prev) => ({ ...prev, [row.company_id]: "Saved" }));
      window.setTimeout(() => {
        setRowFlash((prev) => ({ ...prev, [row.company_id]: "" }));
      }, 1800);
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [row.company_id]: apiErrorMessage(err, "Could not save limits."),
      }));
    } finally {
      setSavingId("");
    }
  };

  if (loading) {
    return (
      <section data-testid="user-limits-loading" style={{ padding: "1.25rem", color: "#6b7280", fontSize: "0.85rem" }}>
        Loading role limits…
      </section>
    );
  }
  if (error) {
    return (
      <section
        data-testid="user-limits-error"
        style={{ padding: "1rem", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", color: "#991b1b", fontSize: "0.85rem" }}
      >
        {error}
      </section>
    );
  }

  const roleKeys = matrix?.role_keys || [];
  const rows = matrix?.rows || [];

  return (
    <section data-testid="user-limits-panel" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#111827" }}>User limits</h2>
          <p style={{ margin: "0.15rem 0 0", fontSize: "0.8rem", color: "#6b7280" }}>
            Max active users per role. Blank = unlimited. Reducing a limit blocks new additions but never deactivates existing users. Owner/Admin can&rsquo;t go below {OWNER_ADMIN_FLOOR}.
          </p>
        </div>
        <button
          type="button"
          data-testid="user-limits-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.35rem",
            background: "transparent", border: "1px solid #d1d5db", borderRadius: "999px",
            padding: "0.3rem 0.8rem", fontSize: "0.8rem", color: "#374151",
            cursor: refreshing ? "wait" : "pointer",
          }}
        >
          <RefreshCw size={13} style={{ opacity: refreshing ? 0.5 : 1 }} />
          Refresh
        </button>
      </header>

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "12px", background: "#fff" }}>
        <table data-testid="user-limits-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={thLeft}>Company</th>
              {roleKeys.map((role) => (
                <th key={role} style={thCenter}>{ROLE_LABELS[role] || role}</th>
              ))}
              <th style={thCenter} aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const dirty = rowIsDirty(row);
              const isSaving = savingId === row.company_id;
              const err = rowError[row.company_id];
              const flash = rowFlash[row.company_id];
              return (
                <tr key={row.company_id} data-testid={`user-limits-row-${row.company_id}`} style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td style={tdLeft}>
                    <div style={{ fontWeight: 600, color: "#111827" }}>{row.display_name || row.name || row.company_id}</div>
                    {err && (
                      <div data-testid={`user-limits-error-${row.company_id}`} style={{ fontSize: "0.72rem", color: "#b91c1c", marginTop: "0.15rem" }}>{err}</div>
                    )}
                    {flash && (
                      <div style={{ fontSize: "0.72rem", color: "#047857", marginTop: "0.15rem" }}>{flash}</div>
                    )}
                  </td>
                  {roleKeys.map((role) => {
                    const usage = row.usage?.[role] ?? 0;
                    const draftVal = drafts[row.company_id]?.[role] ?? "";
                    const cap = draftVal === "" ? null : Number(draftVal);
                    const overCap = cap !== null && !Number.isNaN(cap) && usage > cap;
                    return (
                      <td key={role} style={tdCenter}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "0.15rem" }}>
                          <input
                            type="number"
                            min={role === "owner_admin" ? OWNER_ADMIN_FLOOR : 0}
                            step={1}
                            placeholder="∞"
                            data-testid={`user-limits-input-${row.company_id}-${role}`}
                            value={draftVal}
                            onChange={(e) => changeDraft(row.company_id, role, e.target.value)}
                            style={{
                              width: "72px",
                              padding: "0.25rem 0.4rem",
                              border: `1px solid ${overCap ? "#fca5a5" : "#d1d5db"}`,
                              borderRadius: "6px",
                              textAlign: "center",
                              fontSize: "0.82rem",
                              background: overCap ? "#fef2f2" : "#fff",
                            }}
                          />
                          <span style={{ fontSize: "0.7rem", color: overCap ? "#b91c1c" : "#6b7280" }}>
                            {usage} in use
                          </span>
                        </div>
                      </td>
                    );
                  })}
                  <td style={tdCenter}>
                    <button
                      type="button"
                      data-testid={`user-limits-save-${row.company_id}`}
                      disabled={!dirty || isSaving}
                      onClick={() => saveRow(row)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "0.3rem",
                        background: dirty ? "#111827" : "transparent",
                        color: dirty ? "#fff" : "#9ca3af",
                        border: "1px solid",
                        borderColor: dirty ? "#111827" : "#d1d5db",
                        borderRadius: "999px",
                        padding: "0.3rem 0.9rem",
                        fontSize: "0.78rem",
                        cursor: dirty && !isSaving ? "pointer" : "not-allowed",
                      }}
                    >
                      {isSaving ? "Saving…" : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdLeft, textAlign: "center", color: "#6b7280", padding: "1.5rem" }} colSpan={roleKeys.length + 2}>
                  No companies to display.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const thLeft = {
  padding: "0.6rem 0.85rem",
  textAlign: "left",
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};
const thCenter = { ...thLeft, textAlign: "center" };
const tdLeft = { padding: "0.55rem 0.85rem", textAlign: "left", verticalAlign: "middle", color: "#111827" };
const tdCenter = { ...tdLeft, textAlign: "center" };
